/**
 * Focus Overlay Component
 * Provides visual focus mode by blurring/dimming non-focused groups.
 */

import React from 'react';
import { Box, IconButton, Tooltip, Fade } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';

export interface FocusModeState {
  enabled: boolean;
  focusedTargetIds: Set<string>;
  focusedSourceIds: Set<string>;
}

/**
 * Focus mode toggle button for the toolbar.
 */
export const FocusModeToggle: React.FC<{
  enabled: boolean;
  onToggle: () => void;
  selectionCount?: number;
}> = ({ enabled, onToggle, selectionCount = 0 }) => {
  return (
    <Tooltip title={enabled ? `Exit Focus Mode${selectionCount > 0 ? ` (${selectionCount} selected)` : ''}` : 'Enter Focus Mode'}>
      <IconButton
        onClick={onToggle}
        size="small"
        sx={{
          color: enabled ? 'primary.main' : 'text.secondary',
          backgroundColor: enabled ? 'primary.lighter' : 'transparent',
          position: 'relative',
          '&:hover': {
            backgroundColor: enabled ? 'primary.light' : 'action.hover',
          },
        }}
      >
        {enabled ? <CenterFocusStrongIcon /> : <CenterFocusWeakIcon />}
        {enabled && selectionCount > 0 && (
           <Box sx={{
               position: 'absolute',
               top: 2,
               right: 2,
               width: 6,
               height: 6,
               borderRadius: '50%',
               backgroundColor: 'primary.main',
               border: '1px solid #fff' 
           }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

/**
 * Focus mode indicator badge.
 * Shows which group is currently focused and allows clearing focus.
 */
export const FocusIndicator: React.FC<{
  focusedGroupLabel: string | null;
  onClearFocus: () => void;
}> = ({ focusedGroupLabel, onClearFocus }) => {
  if (!focusedGroupLabel) {
    return null;
  }

  return (
    <Fade in={Boolean(focusedGroupLabel)}>
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: 2,
        }}
      >
        <CenterFocusStrongIcon fontSize="small" />
        <Box sx={{ fontWeight: 500 }}>Focused: {focusedGroupLabel}</Box>
        <Box
          onClick={onClearFocus}
          sx={{
            ml: 1,
            cursor: 'pointer',
            opacity: 0.8,
            '&:hover': { opacity: 1 },
            fontSize: 13,
          }}
        >
          ✕
        </Box>
      </Box>
    </Fade>
  );
};

export default FocusModeToggle;
